import { useEffect } from 'react';
import type { CreateTaskInput, CreateStudentInput, CreateCalendarEventInput } from '@frappi/shared';

import { useUIStore } from '@/stores/ui-store';
import { TaskForm } from '@/features/tasks/components/task-form';
import { StudentForm } from '@/features/students/components/student-form';
import { EventForm } from '@/features/calendar/components/event-form';
import { UpgradeDialog } from '@/components/upgrade-dialog';
import { useCreateTask } from '@/features/tasks/hooks/use-tasks';
import { useCreateStudent } from '@/features/students/hooks/use-students';
import { useCreateCalendarEvent } from '@/features/calendar/hooks/use-calendar';
import { useQuotaCheck } from '@/hooks/use-quota-check';

export function GlobalModals() {
  const {
    createTaskOpen,
    setCreateTaskOpen,
    createStudentOpen,
    setCreateStudentOpen,
    createEventOpen,
    setCreateEventOpen,
    upgradeDialogOpen,
    setUpgradeDialogOpen,
    selectedDate,
  } = useUIStore();

  const createTask = useCreateTask();
  const createStudent = useCreateStudent();
  const createEvent = useCreateCalendarEvent();

  const taskQuota = useQuotaCheck('tasks');
  const studentQuota = useQuotaCheck('students');

  // Proactive check: intercept form opens when limit is reached
  useEffect(() => {
    if (createTaskOpen && !taskQuota.allowed && !taskQuota.isPremium) {
      setCreateTaskOpen(false);
      setUpgradeDialogOpen(true);
    }
  }, [createTaskOpen, taskQuota.allowed, taskQuota.isPremium, setCreateTaskOpen, setUpgradeDialogOpen]);

  useEffect(() => {
    if (createStudentOpen && !studentQuota.allowed && !studentQuota.isPremium) {
      setCreateStudentOpen(false);
      setUpgradeDialogOpen(true);
    }
  }, [createStudentOpen, studentQuota.allowed, studentQuota.isPremium, setCreateStudentOpen, setUpgradeDialogOpen]);

  const handleCreateTask = (data: CreateTaskInput) => {
    createTask.mutate(data, {
      onSuccess: () => setCreateTaskOpen(false),
    });
  };

  const handleCreateStudent = (data: CreateStudentInput) => {
    createStudent.mutate(data, {
      onSuccess: () => setCreateStudentOpen(false),
    });
  };

  const handleCreateEvent = (data: CreateCalendarEventInput) => {
    createEvent.mutate(data, {
      onSuccess: () => setCreateEventOpen(false),
    });
  };

  return (
    <>
      <TaskForm
        open={createTaskOpen}
        onOpenChange={setCreateTaskOpen}
        onSubmit={handleCreateTask}
        isLoading={createTask.isPending}
      />
      <StudentForm
        open={createStudentOpen}
        onOpenChange={setCreateStudentOpen}
        onSubmit={handleCreateStudent}
        isLoading={createStudent.isPending}
      />
      <EventForm
        open={createEventOpen}
        onOpenChange={setCreateEventOpen}
        defaultDate={selectedDate}
        onSubmit={handleCreateEvent}
        isLoading={createEvent.isPending}
      />
      <UpgradeDialog
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
      />
    </>
  );
}
